import { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAudioPlayer, useSpeechRecognition } from '../hooks/useAudio';
import { api } from '../lib/api';
import { SophiaAvatar } from '../components/SophiaAvatar';
import { LanguageSelector } from '../components/LanguageSelector';
import { ShareWisdomButton } from '../components/ShareWisdomCard';
import { Button } from '../components/ui/button';
import { ArrowLeft, Mic, Send, Sparkles, Volume2, ImageIcon } from 'lucide-react';

export default function AdvicePage() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const { play, stop } = useAudioPlayer();
  const { startListening, stopListening } = useSpeechRecognition(language);
  const [input, setInput] = useState('');
  const [advice, setAdvice] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const currentAudioRef = useRef(null);

  const playAudio = useCallback((url) => {
    if (!url) return;
    
    // Stop any current audio
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }
    
    setIsSpeaking(true);
    const audio = new Audio(url);
    currentAudioRef.current = audio;
    
    audio.onended = () => {
      setIsSpeaking(false);
      currentAudioRef.current = null;
    };
    audio.onerror = () => {
      setIsSpeaking(false);
      currentAudioRef.current = null;
    };
    
    audio.play().catch((e) => {
      console.error('Audio play error:', e);
      setIsSpeaking(false);
    });
  }, []);

  const getAdvice = async (text) => {
    if (!text.trim() || isLoading) return;
    setIsLoading(true);
    setAdvice(null);
    setAudioUrl(null);
    setIsSpeaking(false);
    
    try {
      const { data } = await api.getAdvice(text, language);
      setAdvice(data.response);
      
      if (data.audio_url) {
        setAudioUrl(data.audio_url);
        playAudio(data.audio_url);
      }
    } catch (err) {
      console.error('Advice error:', err);
      const errMsg = err.response?.data?.detail || "Even my wisdom needs a moment. Try again, darling.";
      setAdvice(errMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const replayAudio = () => {
    if (audioUrl) {
      playAudio(audioUrl);
    }
  };

  const toggleListening = () => {
    if (isListening) {
      stopListening();
      setIsListening(false);
    } else {
      const started = startListening(
        (transcript) => {
          setInput(transcript);
          getAdvice(transcript);
        },
        () => setIsListening(false)
      );
      if (started) setIsListening(true);
    }
  };

  const shareAdvice = async () => {
    if (!advice) return;
    const text = `"${advice}" — Sophia Einstein\n@sophia_einstein_the_wise`;
    
    // Use native share API (works on iOS/Android)
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Sophia Einstein Wisdom',
          text: text,
        });
      } catch (err) {
        // User cancelled or share failed - fallback to clipboard
        if (err.name !== 'AbortError') {
          copyToClipboard(text);
        }
      }
    } else {
      // Fallback for desktop - copy to clipboard
      copyToClipboard(text);
    }
  };

  const copyToClipboard = (text) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).catch(() => {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      });
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col relative overflow-hidden noise-overlay">
      {/* Background gradient orbs */}
      <div className="absolute top-[-20%] right-[-10%] w-[400px] h-[400px] bg-sophia-pink-500/8 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-[-15%] left-[-10%] w-[350px] h-[350px] bg-mystic-teal-500/8 rounded-full blur-3xl pointer-events-none" />

      {/* Header */}
      <motion.header
        className="flex items-center justify-between px-6 py-4 border-b border-border/50 backdrop-blur-xl bg-background/90 relative z-30"
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
      >
        <Button
          data-testid="advice-back-btn"
          variant="ghost"
          size="icon"
          onClick={() => navigate('/')}
          className="rounded-full"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="font-display text-xl font-semibold">
          {language === 'de' ? 'Schneller Rat' : 'Quick Advice'}
        </h1>
        <LanguageSelector />
      </motion.header>

      {/* Main content */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-6 gap-6 max-w-lg mx-auto w-full">
        {/* Sophia Avatar */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 200, damping: 20 }}
          className="flex justify-center"
        >
          <SophiaAvatar variant="pink" size="xl" isSpeaking={isSpeaking} />
        </motion.div>

        {/* Advice result */}
        <AnimatePresence mode="wait">
          {advice && (
            <motion.div
              key="advice"
              data-testid="advice-response"
              className="bg-card border border-border/50 rounded-3xl p-6 shadow-lg text-center"
              initial={{ opacity: 0, y: 30, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ type: 'spring', stiffness: 200, damping: 25 }}
            >
              <p className="font-body text-lg leading-relaxed italic text-foreground">
                "{advice}"
              </p>
              <p className="font-display text-sophia-pink-500 text-sm mt-3 italic">— Sophia</p>

              {/* Audio replay and share buttons */}
              <div className="flex items-center justify-center gap-3 mt-5">
                {audioUrl && (
                  <button
                    data-testid="advice-replay-btn"
                    onClick={replayAudio}
                    className="flex items-center gap-2 px-4 py-2 rounded-full bg-mystic-teal-500/10 text-mystic-teal-500 border border-mystic-teal-500/20 text-sm font-body hover:bg-mystic-teal-500/20 transition-all"
                  >
                    <Volume2 className="w-4 h-4" />
                    {language === 'de' ? 'Nochmal hören' : 'Listen again'}
                  </button>
                )}
                {/* Share as Wisdom Card */}
                <ShareWisdomButton 
                  quote={advice} 
                  language={language} 
                  variant="full"
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Input area */}
        <motion.div
          className="w-full space-y-4 mt-auto"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <div className="flex items-center gap-3">
            <motion.button
              data-testid="advice-mic-btn"
              onClick={toggleListening}
              className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 shrink-0 ${
                isListening
                  ? 'bg-sophia-pink-500 text-white shadow-[0_0_20px_rgba(242,145,175,0.5)]'
                  : 'bg-card border border-border text-muted-foreground hover:text-foreground hover:border-sophia-pink-500/50'
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.9 }}
            >
              <Mic className={`w-5 h-5 ${isListening ? 'animate-pulse' : ''}`} />
            </motion.button>

            <input
              data-testid="advice-text-input"
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && getAdvice(input)}
              placeholder={
                isListening
                  ? (language === 'de' ? 'Ich höre zu...' : "I'm listening...")
                  : (language === 'de' ? 'Was beschäftigt dich, Liebling?' : "What's on your mind, darling?")
              }
              className="flex-1 h-12 px-5 rounded-full bg-card border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-sophia-pink-500/50 font-body text-sm"
            />

            <motion.button
              data-testid="advice-submit-btn"
              onClick={() => getAdvice(input)}
              disabled={!input.trim() || isLoading}
              className="w-12 h-12 rounded-full bg-sophia-pink-500 text-white flex items-center justify-center disabled:opacity-40 hover:bg-sophia-pink-700 transition-colors shrink-0"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.9 }}
            >
              {isLoading ? (
                <Sparkles className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </motion.button>
          </div>

          <p className="text-center text-xs text-muted-foreground font-body">
            {language === 'de'
              ? 'Frag Sophia nach einem kurzen, witzigen Rat'
              : 'Ask Sophia for a quick, witty piece of wisdom'}
          </p>
        </motion.div>
      </div>
    </div>
  );
}
